import {
  Box,
  Button,
  ButtonProps,
  HStack,
  Heading,
  IconButton,
  LinkBox,
  LinkOverlay,
  Popover,
  Portal,
  RadioGroup,
  Text,
  VStack
} from "@chakra-ui/react"
import {
  CoverPageOfferFragment as CoverPageOffer,
  OfferAccessState,
  OfferScope
} from "@pathwright/graphql-client/src/generated"
import { ChevronRightIcon, SparklesIcon } from "@pathwright/pathicons"
import { augmentationSectionFocusTarget } from "@pathwright/ux/src/components/augmentation/sectionFocus"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import Link, {
  LinkRelativeTo,
  LinkTo
} from "@pathwright/ux/src/components/link/Link"
import HTMLText from "@pathwright/ux/src/components/text/HTMLText"
import { onMeasureType } from "@pathwright/ux/src/components/text/TruncatedHTML"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import moment from "moment"
import { Fragment, useMemo, useState } from "react"
import { Link as ReactRouterLink, useRouteMatch } from "react-router-dom"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { AUGMENTATION_PANEL_QUERY_PARAM } from "../utils/urls"
import { getOfferConversionPanelPath } from "./coverPageOfferPayment"
import CoverPageContinueButton from "./CoverPageContinueButton"
import CoverPageOfferShare, {
  canShareCoverPageOffer
} from "./CoverPageOfferShare"
import { useCoverPageHasLoadedResource } from "./useCoverPageHasLoadedResource"
import CoverPageDivider from "./CoverPageDivider"
import CoverPagePathOfferButton from "./CoverPagePathOfferButton"
import { useCoverPageContext } from "./CoverPageProvider"
import { useCoverPageIsEditable } from "./access"
import {
  getCoverPageAnotherActionLabels,
  getPrivateCohortAnotherAction
} from "./coverPageAnotherAction"
import { coverPageContinueShowsMeta } from "./coverPageContinue"
import { getOfferContinueOptions } from "./coverPageOfferOrdering"
import {
  CoverPageSectionTriggerButton,
  CoverPageSectionTriggerGroup
} from "./design/CoverPageSectionTrigger"
import { ImmersiveTriggerButton } from "./immersive/CoverPageImmersive"
import { useCoverPageContinue } from "./useCoverPageContinue"
import { useCreatePathFromOffer } from "./useCreatePathFromOffer"
import { offerShowsBottomMetaRow, useCoverPageTranslate } from "./utils"

const dividerHeight = 21 // CoverPageDivider height

export type OfferButtonRenderProps = Partial<ButtonProps> & {
  to?: LinkTo
  relativeTo?: LinkRelativeTo
  href?: string
  target?: string
  rel?: string
}

export const offerButtonProps: OfferButtonRenderProps = {
  variant: "solid",
  colorPalette: "brand",
  textDecoration: "none",
  fontWeight: "700",
  size: "xl",
  _hover: { textDecoration: "none", opacity: 0.9 }
}

/** Augmentation-panel path for converting/creating from this Offer. */
export function getOfferConversionRelativeTo(
  offer: Pick<CoverPageOffer, "id" | "price_options">,
  selectedOptionIndex: number = 0,
  inviteToken?: string | null
): string {
  const selectedOption = offer.price_options[selectedOptionIndex]

  // Pass the selected payment type so RegisterFlow can choose in-plan vs
  // one-off conversion. This is required even for a single option: an
  // active subscriber sees one "included in your subscription" option,
  // and omitting paymentType makes RegisterFlow default to the one-off
  // Stripe Checkout path.
  // Keep the remembered invite in the href so reloads and new tabs retain it.
  return `?${AUGMENTATION_PANEL_QUERY_PARAM}=${getOfferConversionPanelPath(
    offer.id,
    selectedOption?.payment_type
  )}${inviteToken ? `&ic=${encodeURIComponent(inviteToken)}` : ""}`
}

export const OfferButton = ({
  offer,
  selectedOptionIndex,
  buttonProps: buttonPropsOverride,
  renderButton
}: {
  offer: CoverPageOffer
  selectedOptionIndex: number
  buttonProps?: OfferButtonRenderProps
  renderButton?: (props: OfferButtonRenderProps) => React.ReactNode
}) => {
  const { url } = useRouteMatch()
  const hasLoadedResource = useCoverPageHasLoadedResource()
  const { t } = useCoverPageTranslate()
  const { immersiveActive, setOfferConversionIntentId, inviteToken } =
    useCoverPageContext()
  const resourceId = useCoverPageContext((v) => v.resourceId)
  const slug = useCoverPageContext((v) => v.paintResource?.slug)
  const selectedOption = offer.price_options[selectedOptionIndex]
  const hasAccess = offer.access.state === OfferAccessState.Active
  const isUnavailable = offer.access.state === OfferAccessState.Unavailable
  const continueState = useCoverPageContinue(
    resourceId,
    getOfferContinueOptions(offer)
  )

  const conversionRelativeTo = getOfferConversionRelativeTo(
    offer,
    selectedOptionIndex,
    inviteToken
  )

  const buttonProps = useMemo(() => {
    const props: OfferButtonRenderProps = {
      children:
        selectedOption.button_label ||
        t("offer.continue", { defaultValue: "Continue" }),
      w: "100%",
      className: "CoverPageOfferButton",
      ...offerButtonProps,
      ...buttonPropsOverride
    }

    // Legacy Offer.access Continue — only when RegistrationAccess is not ready
    // or reports no matching rows for this Offer.
    if (hasAccess && continueState.mode === "none") {
      if (!slug) {
        props.disabled = true
      } else {
        props.disabled = false
        props.as = ReactRouterLink // Link: TODO: fix odd styles when using Link
        props.to = `/library/${slug}/${offer.cohort_id!}/path/`
        props.children = (
          <>
            {selectedOption.button_label ||
              t("offer.continue", { defaultValue: "Continue" })}
            <ChevronRightIcon />
          </>
        )
      }
    } else if (isUnavailable || !hasLoadedResource) {
      // Convert / Join CTA only. RegistrationAccess Continue (below) must stay
      // enabled even when Offer.access is Unavailable — e.g. subscription_expired
      // is resumable and opens the same expired flow as Home.
      props.disabled = true
    } else {
      props.relativeTo = conversionRelativeTo
    }

    if (!hasAccess && !hasLoadedResource) {
      // Render a native disabled button, never a navigable pending anchor.
      props.as = undefined
      props.to = undefined
      props.href = undefined
      props.relativeTo = undefined
      props._disabled = { ...props._disabled, pointerEvents: "auto" }
    }

    props.onPointerDown = () => {
      if (props.disabled) return
      // Set conversion intent before the Link navigates so the panel does not
      // mount with attemptToConvert=false (Register CTA, no auto-submit).
      setOfferConversionIntentId(offer.id || null)
    }
    props.onClick = (e: React.MouseEvent) => {
      // Prevent the card wrapper's onClick (which opens the offer immersive route)
      // from hijacking this button's navigation to /register.
      e.stopPropagation()

      if (!props.disabled) setOfferConversionIntentId(offer.id || null)
    }

    return props
  }, [
    hasAccess,
    hasLoadedResource,
    continueState.mode,
    isUnavailable,
    offer,
    immersiveActive,
    url,
    buttonPropsOverride,
    slug,
    selectedOption.button_label,
    conversionRelativeTo,
    setOfferConversionIntentId,
    t
  ])

  // Path Offers own their create + continue UI (in-place licensing).
  if (offer.scope === OfferScope.Path) {
    return (
      <CoverPagePathOfferButton
        offer={offer}
        renderButton={renderButton}
        {...buttonProps}
      />
    )
  }

  // RegistrationAccess Continue takes priority over create once known.
  if (continueState.ready && continueState.mode !== "none") {
    const anotherAction = getPrivateCohortAnotherAction({
      offer,
      accesses: continueState.accesses
    })
    const anotherLabels = getCoverPageAnotherActionLabels(anotherAction.kind)
    return (
      <CoverPageContinueButton
        accesses={continueState.accesses}
        canCreateAnother={anotherAction.show}
        anotherActionLabel={
          anotherLabels
            ? t(anotherLabels.ellipsisKey, {
                defaultValue: anotherLabels.ellipsisDefault
              })
            : undefined
        }
        createAnotherRelativeTo={
          anotherAction.show ? conversionRelativeTo : undefined
        }
        onCreateAnotherIntent={() =>
          setOfferConversionIntentId(offer.id || null)
        }
        buttonProps={buttonProps}
        renderButton={renderButton}
      />
    )
  }

  const button =
    "relativeTo" in buttonProps && buttonProps.relativeTo ? (
      <Button asChild {...buttonProps}>
        <Link relativeTo={buttonProps.relativeTo}>{buttonProps.children}</Link>
      </Button>
    ) : (
      <Button {...buttonProps} />
    )

  return renderButton ? renderButton(buttonProps) : button
}

type OfferPricingProps = {
  offer: CoverPageOffer
  reserveMultipleOptionsHeight?: boolean
  /** When true, render RegistrationAccess Continue meta under the CTA. */
  displayContinueMeta?: boolean
}

const OfferPricing = ({
  offer,
  reserveMultipleOptionsHeight,
  displayContinueMeta
}: OfferPricingProps) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0)

  const handleChange = (idx: number) => {
    setSelectedOptionIndex(idx)
  }

  return (
    <VStack gap="5px" align="center" className="OfferPricing" w="100%">
      <OfferMultipleOptions
        offer={offer}
        selectedOptionIndex={selectedOptionIndex}
        onChangeSelectedOptionIndex={handleChange}
        reserveMultipleOptionsHeight={reserveMultipleOptionsHeight}
      />
      <OfferButton offer={offer} selectedOptionIndex={selectedOptionIndex} />
      {displayContinueMeta ? (
        <OfferContinueMeta
          offer={offer}
          selectedOptionIndex={selectedOptionIndex}
        />
      ) : (
        <OfferPriceDescription
          offer={offer}
          selectedOptionIndex={selectedOptionIndex}
        />
      )}
    </VStack>
  )
}

const OfferMultipleOptions = ({
  offer,
  selectedOptionIndex,
  onChangeSelectedOptionIndex,
  reserveMultipleOptionsHeight
}: {
  offer: CoverPageOffer
  selectedOptionIndex: number
  onChangeSelectedOptionIndex: (index: number) => void
  reserveMultipleOptionsHeight?: boolean
}) => {
  const hasMultipleOptions = (offer.price_options.length || 0) > 1
  const shouldReserveSpace = reserveMultipleOptionsHeight || hasMultipleOptions

  if (!shouldReserveSpace) return null

  if (!hasMultipleOptions) {
    return <Box h={`${OFFER_MULTIPLE_OPTIONS_HEIGHT}px`} w="100%" />
  }

  return (
    <Box h={`${OFFER_MULTIPLE_OPTIONS_HEIGHT}px`} w="100%" display="flex">
      <RadioGroup.Root
        value={selectedOptionIndex.toString()}
        onValueChange={({ value }) =>
          onChangeSelectedOptionIndex(Number(value))
        }
        size="sm"
      >
        <VStack
          gap={2}
          align="start"
          css={{
            "& .chakra-radio__control": {
              height: "1rem !important",
              width: "1rem !important"
            },
            "& .chakra-radio__label": {
              marginLeft: "3px"
            }
          }}
        >
          {offer.price_options.map((option, index) => (
            <RadioGroup.Item key={index} value={index.toString()}>
              <RadioGroup.ItemHiddenInput />
              <RadioGroup.ItemIndicator />
              <RadioGroup.ItemText>
                {option.option_label || option.button_label}
              </RadioGroup.ItemText>
            </RadioGroup.Item>
          ))}
        </VStack>
      </RadioGroup.Root>
    </Box>
  )
}

const OfferPriceDescription = ({
  offer,
  selectedOptionIndex
}: {
  offer: CoverPageOffer
  selectedOptionIndex: number
}) => {
  const selectedOption = offer.price_options[selectedOptionIndex]
  const hasMultipleOptions = (offer.price_options.length || 0) > 1

  if (hasMultipleOptions || !selectedOption.price_description) return null

  return (
    <Box
      m={0}
      lineClamp={1}
      as="span"
      fontSize="sm"
      lineHeight="1 !important"
      fontWeight="bold"
      dangerouslySetInnerHTML={{
        __html: selectedOption.price_description
      }}
    />
  )
}
// OfferBody: Renders the offer description with overflow detection.
// Cross-card CTA alignment comes from flex on the card; maxHeight is only
// for TruncatedHTML when the collection exceeds MAX_CARD_HEIGHT.
const OfferBody = ({
  offer,
  maxHeight,
  onMeasureBody
}: {
  offer: CoverPageOffer
  maxHeight?: number
  onMeasureBody?: onMeasureType
}) => {
  const truncate = maxHeight != null

  return (
    <Box
      position="relative"
      h="100%"
      maxH="100%"
      overflow="hidden"
      minH={0}
      css={{
        "& .HTMLText": {
          height: "100%"
        },
        "& .TruncatedHTML-more-button": {
          pt: 2
        }
      }}
    >
      <HTMLText
        html={offer.description || ""}
        truncate={truncate}
        truncateProps={
          truncate
            ? {
                maxHeight,
                onMeasure: onMeasureBody,
                renderMore: offer.id
                  ? () => (
                      <ImmersiveTriggerButton
                        relativeTo={`offer/${offer.id}/`}
                      />
                    )
                  : undefined
              }
            : undefined
        }
      />
    </Box>
  )
}

// OfferMeta: Renders the access meta information
const OfferMeta = ({ offer }: { offer: CoverPageOffer }) => {
  if (offer.access.meta.length === 0) return null

  return (
    <HStack
      className="OfferMeta"
      gap={1}
      align="stretch"
      justify="center"
      alignItems="center"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      maxW="100%"
      lineHeight="1"
      py="1px"
    >
      {offer.access.meta.map((meta, index) => (
        <Fragment key={`offer-meta-${index}`}>
          {index > 0 && (
            <Text
              as="span"
              fontSize="md"
              mx={0}
              lineHeight="1 !important"
              mt={"-3px"}
            >
              &bull;
            </Text>
          )}
          <Text
            key={index}
            fontSize="sm"
            m={0}
            w="max-content"
            as="span"
            lineHeight="1 !important"
          >
            {meta}
          </Text>
        </Fragment>
      ))}
    </HStack>
  )
}

const OfferConversionMeta = ({ offer }: { offer: CoverPageOffer }) => {
  const { t } = useCoverPageTranslate()
  const { me } = usePathwrightContext()

  return me ? (
    <HStack pos="relative" alignItems="center" zIndex={1} gap={1}>
      <UserAvatar user={me} w="1.2rem" h="1.2rem" />
      <Tooltip
        // NOTE: the offer price_options and meta are not always present. Seems we don't provide them when
        // the user has access to the offer.
        title={[
          ...offer.price_options.map(
            (option) => option.option_label || option.button_label
          ),
          ...offer.access.meta
        ].join(" • ")}
      >
        <Text
          as="span"
          m={0}
          fontSize="sm"
          w="max-content"
          lineHeight="1 !important"
        >
          {t("offer.access.joined_on", {
            defaultValue: "Join on {{date}}",
            date: moment(offer.access.conversion_date).format("MMM D, YYYY")
          })}
        </Text>
      </Tooltip>
    </HStack>
  ) : null
}

const OfferPathCreateAnotherLink = ({
  offer,
  label
}: {
  offer: CoverPageOffer
  label: string
}) => {
  const createPath = useCreatePathFromOffer(offer)
  const hasLoadedResource = useCoverPageHasLoadedResource()
  return (
    <Button
      variant="plain"
      size="sm"
      h="auto"
      minW="unset"
      p={0}
      m={0}
      fontSize="sm"
      fontWeight="normal"
      lineHeight="1"
      textDecoration="underline"
      color="inherit"
      loading={createPath.loading}
      disabled={!hasLoadedResource || createPath.loading}
      _disabled={{ pointerEvents: "auto" }}
      onClick={hasLoadedResource ? createPath.create : undefined}
      onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
    >
      {label}
    </Button>
  )
}

/** Meta under Continue: Created/Joined date, or Access to N groups. */
const OfferContinueMeta = ({
  offer,
  selectedOptionIndex
}: {
  offer: CoverPageOffer
  selectedOptionIndex: number
}) => {
  const { t } = useCoverPageTranslate()
  const { setOfferConversionIntentId, inviteToken } = useCoverPageContext()
  const hasLoadedResource = useCoverPageHasLoadedResource()
  const resourceId = useCoverPageContext((v) => v.resourceId)
  const continueState = useCoverPageContinue(
    resourceId,
    getOfferContinueOptions(offer)
  )
  const conversionRelativeTo = getOfferConversionRelativeTo(
    offer,
    selectedOptionIndex,
    inviteToken
  )

  if (!coverPageContinueShowsMeta(continueState)) return null

  if (continueState.mode === "multiple") {
    return null
  }

  const primary = continueState.accesses[0]
  if (!primary) return null

  const date = moment(primary.created).format("MMM D, YYYY")
  const metaText =
    continueState.singleMetaVerb === "created"
      ? t("offer.access.created_on", {
          defaultValue: "Created {{date}}",
          date
        })
      : t("offer.access.joined_on_short", {
          defaultValue: "Joined {{date}}",
          date
        })

  const anotherAction = getPrivateCohortAnotherAction({
    offer,
    accesses: continueState.accesses
  })
  const anotherLabels = getCoverPageAnotherActionLabels(anotherAction.kind)
  const anotherQuestionLabel = anotherLabels
    ? t(anotherLabels.questionKey, {
        defaultValue: anotherLabels.questionDefault
      })
    : null

  return (
    <VStack gap={0} align="center" zIndex={1}>
      <Text
        as="span"
        m={0}
        fontSize="sm"
        w="max-content"
        lineHeight="1 !important"
      >
        {metaText}
      </Text>
      {anotherAction.show &&
        anotherQuestionLabel &&
        (offer.scope === OfferScope.Path ? (
          <OfferPathCreateAnotherLink
            offer={offer}
            label={anotherQuestionLabel}
          />
        ) : (
          <Button
            asChild={hasLoadedResource}
            variant="plain"
            size="sm"
            h="auto"
            minW="unset"
            p={0}
            m={0}
            fontSize="sm"
            fontWeight="normal"
            lineHeight="1 !important"
            textDecoration="underline"
            color="inherit"
            disabled={!hasLoadedResource}
            _disabled={{ pointerEvents: "auto" }}
          >
            {hasLoadedResource ? (
              <Link
                relativeTo={conversionRelativeTo}
                onPointerDown={() =>
                  setOfferConversionIntentId(offer.id || null)
                }
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  setOfferConversionIntentId(offer.id || null)
                }}
              >
                {anotherQuestionLabel}
              </Link>
            ) : (
              anotherQuestionLabel
            )}
          </Button>
        ))}
    </VStack>
  )
}

export const InspectOffering = ({ offer }: { offer: CoverPageOffer }) => {
  const { t } = useCoverPageTranslate()

  return (
    <Popover.Root positioning={{ placement: "right-end" }}>
      <Popover.Trigger>
        <IconButton
          aria-label={t("offer.inspect_offering", {
            defaultValue: "Inspect Offering"
          })}
          className="inspect-offering-trigger"
          children={<SparklesIcon size="0.9rem" />}
          size="sm"
          variant="ghost"
          position="absolute"
          top={4}
          right={4}
          zIndex={2}
          cursor="pointer"
          opacity={0}
          transition="opacity 0.2s, pointer-events 0.2s"
        />
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            bg="gray.100"
            width="xl"
            top={"5vh"}
            maxW="90vw"
            maxH="90vh"
            overflow="auto"
            p={6}
          >
            <Popover.Arrow />
            <Popover.Body>
              <Text fontSize="sm" fontWeight="bold" mb={2} fontFamily="mono">
                {t("offer.offering_data", { defaultValue: "Offering Data" })}
              </Text>
              <Box
                as="pre"
                fontSize="xs"
                fontFamily="mono"
                whiteSpace="pre-wrap"
                m={0}
              >
                {JSON.stringify(offer, null, 2)}
              </Box>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

// Main offer card component (default export, keep as CoverPageOffer)
type CoverPageOfferCardProps = {
  offer: CoverPageOffer
  showInspectOffering?: boolean
  /** Reserve bottom meta row height across the collection. */
  displayMeta?: boolean
  /** Reserve price-description line height across the collection. */
  displayDescription?: boolean
  /** Reserve multi-option radio height across the collection. */
  reserveMultipleOptionsHeight?: boolean
  /** Reserve Create another? line across the collection when any card shows it. */
  reserveContinueCreateAnother?: boolean
  /** TruncatedHTML budget when the collection exceeds MAX_CARD_HEIGHT. */
  maxBodyHeightForTruncation?: number
  /** Soft cap on card height (carousel maxItemHeight). */
  maxCardHeight?: number
  onMeasureBody?: onMeasureType
}

// Chrome constants used by collection truncation budget (CoverPageOffers).
export const OFFER_TITLE_HEIGHT = 19.55 // 1.15rem * 17px
export const OFFER_HEADING_SPACER = 8 // 0.5rem * 16px
export const OFFER_PRICE_DESCRIPTION_HEIGHT = 12 // 0.75rem * 16px (sm)
export const OFFER_PRICING_SPACER = 5 // gap
export const OFFER_MULTIPLE_OPTIONS_HEIGHT = 52 // 2 radio rows + inter-row spacing
export const OFFER_META_HEIGHT = 20 // sm line box incl. py (measured ~20px)
/** Extra line under Continue meta for the underlined "Create another?" action. */
export const OFFER_CONTINUE_CREATE_ANOTHER_HEIGHT = 16
export const OFFER_META_SPACER = 5 // gap
export const OFFER_BUTTON_HEIGHT = 47.59 // Button height

const CoverPageOfferCard = ({
  offer,
  showInspectOffering,
  displayMeta,
  displayDescription,
  reserveMultipleOptionsHeight,
  reserveContinueCreateAnother,
  maxCardHeight,
  maxBodyHeightForTruncation,
  onMeasureBody
}: CoverPageOfferCardProps) => {
  const { immersiveActive, offer: currentOffer } = useCoverPageContext()
  const resourceId = useCoverPageContext((v) => v.resourceId)

  const coverPageIsEditable = useCoverPageIsEditable()
  const continueState = useCoverPageContinue(
    resourceId,
    getOfferContinueOptions(offer)
  )
  const showContinueMeta = coverPageContinueShowsMeta(continueState)
  const showConversionMeta =
    !showContinueMeta && Boolean(offer.access.conversion_date)
  const showBottomMetaRow = showContinueMeta || offerShowsBottomMetaRow(offer)

  // Collection-driven reserved chrome so CTA bands align across cards.
  const priceDescriptionHeight = displayDescription
    ? OFFER_PRICE_DESCRIPTION_HEIGHT
    : 0
  const pricingSpacer = displayDescription ? OFFER_PRICING_SPACER : 0
  const metaHeight = displayMeta
    ? OFFER_META_HEIGHT +
      (reserveContinueCreateAnother ? OFFER_CONTINUE_CREATE_ANOTHER_HEIGHT : 0)
    : 0
  const metaSpacer = displayMeta ? OFFER_META_SPACER : 0
  const hasMultipleOptions = (offer.price_options.length || 0) > 1
  const shouldReserveMultipleOptionsHeight =
    reserveMultipleOptionsHeight || hasMultipleOptions
  const multipleOptionsHeight = shouldReserveMultipleOptionsHeight
    ? OFFER_MULTIPLE_OPTIONS_HEIGHT
    : 0
  const multipleOptionsSpacer = shouldReserveMultipleOptionsHeight
    ? OFFER_PRICING_SPACER
    : 0

  const bottomMinHeight =
    multipleOptionsHeight +
    multipleOptionsSpacer +
    OFFER_BUTTON_HEIGHT +
    priceDescriptionHeight +
    pricingSpacer +
    metaHeight +
    metaSpacer

  const highlightOffer = currentOffer?.id === offer.id && !immersiveActive
  const showShare = !immersiveActive && canShareCoverPageOffer(offer)
  const actionCount = immersiveActive
    ? 0
    : Number(!!coverPageIsEditable) + Number(showShare)

  return (
    <LinkBox
      className="CoverPageOfferCardWrapper"
      p={4} // show the box shadow inside the parent item
      cursor={immersiveActive ? "default" : "pointer"}
      h="100%"
    >
      <VStack
        gap={0}
        className="CoverPageOfferCard"
        data-offer-id={offer.id}
        {...augmentationSectionFocusTarget()}
        align="stretch"
        borderRadius="card"
        bg="white"
        p={4} // content padding
        maxH={maxCardHeight ? `${maxCardHeight}px` : "100%"}
        h="100%"
        shadow={highlightOffer ? "cardHover" : "card"}
        colorPalette="brand"
        outline="2px solid"
        outlineColor={highlightOffer ? "colorPalette.focusRing" : "transparent"}
        outlineOffset="2px"
        _hover={{
          shadow: "cardHover",
          "& .inspect-offering-trigger": { opacity: 1 }
        }}
        transition="all 0.2s"
        position="relative"
      >
        {offer && showInspectOffering && <InspectOffering offer={offer} />}
        <CoverPageSectionTriggerGroup
          top={2}
          right={2}
          revealWithin=".CoverPageOfferCard"
          onClick={(event) => event.stopPropagation()}
        >
          {showShare && <CoverPageOfferShare offer={offer} />}
          <CoverPageSectionTriggerButton
            sectionKey="offers"
            panelItemId={offer.id}
            includeLabel={false}
            preventScrollHighlight
            itemName={offer.title}
          />
        </CoverPageSectionTriggerGroup>

        {/* Title */}
        <LinkOverlay asChild>
          <Link
            relativeTo={immersiveActive || !offer ? "" : `/offer/${offer.id}/`}
            aria-current={highlightOffer ? "true" : undefined}
            justifyContent="flex-start"
            textDecor="none"
            flexShrink={0}
          >
            <Heading
              lineClamp={1}
              as="h4"
              // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
              size="h7"
              mt={0}
              mb={"8px"}
              maxW="100%"
              pr={actionCount === 2 ? 20 : actionCount === 1 ? 10 : 0}
              className="theme-heading theme-heading-semibold"
              flexShrink={0}
            >
              {offer.title}
            </Heading>
          </Link>
        </LinkOverlay>

        {/* Body grows so divider + CTA pin to the bottom of equal-height cards */}
        <Box
          className="OfferBodyWrapper"
          flex="1"
          minH={0}
          overflow="hidden"
          cursor="text"
          onClick={(event) => {
            event.stopPropagation()
          }}
        >
          <OfferBody
            offer={offer}
            maxHeight={immersiveActive ? undefined : maxBodyHeightForTruncation}
            onMeasureBody={onMeasureBody}
          />
        </Box>

        {/* Divider */}
        <Box flexShrink={0} h={`${dividerHeight}px`}>
          <CoverPageDivider verticalMargin="10px" horizontalMargin="0px" />
        </Box>

        {/* Bottom section — reserved min height aligns CTAs across the collection */}
        <VStack
          className="OfferBottomSection"
          flexShrink={0}
          align="center"
          gap={metaSpacer > 0 ? `${metaSpacer}px` : 0}
          minH={`${bottomMinHeight}px`}
          justify="flex-start"
          w="100%"
        >
          <OfferPricing
            offer={offer}
            reserveMultipleOptionsHeight={shouldReserveMultipleOptionsHeight}
            displayContinueMeta={showContinueMeta}
          />
          {showBottomMetaRow &&
            !showContinueMeta &&
            (showConversionMeta ? (
              <OfferConversionMeta offer={offer} />
            ) : (
              <OfferMeta offer={offer} />
            ))}
        </VStack>
      </VStack>
    </LinkBox>
  )
}

export const CoverPageOfferImmersive = ({ id }: { id?: number }) => {
  const { offers } = useCoverPageContext()

  if (!offers?.length) return null

  let offer = offers?.find((offer) => offer.id === Number(id))
  if (!offer) offer = offers[0]

  return (
    <Box
      w="100%"
      h="100%"
      pos="relative"
      px={{ base: 0, sm: "20px", md: "40px" }}
      maxW="800px"
      mx="auto"
    >
      <CoverPageOfferCard
        offer={offer}
        displayMeta={true}
        displayDescription={true}
      />
    </Box>
  )
}

export default CoverPageOfferCard
